/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {isEUProduction} from '@ion/core/src/environment/environment';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {Measurement} from '@ion/core/src/units/attributes';
import {lbsToKg} from '@ion/core/src/units/conversions';
import {
  convertDistance,
  convertHeight,
  convertSpeed,
  convertVolume,
  convertWeight,
  getUserUnit,
  isImperial
} from '@ion/core/src/units/helpers';
import {
  Unit
} from '@ion/core/src/units/units';
import {genericUnitConversion, getTemperatureUnit} from '@ion/core/src/utils/Conversions';

export enum MSS_STATUS {
  OFF = '0',
  AUTO = '1',
  UNLOAD = '2'
};

export enum MEASURES {
  LITER = 'ltr',
  HOURS = 'hrs',
  GAL = 'gal',
  LBS = 'lbs',
  LBS_MILE = 'lbs/mile',
  MILE = 'mi',
  KG = 'kg'
}

interface DickeyJohnFormatterProps {
  type: string;
  value: string;
  channel?: string;
}

// formatGranularTotal formats the granular total based on the user's preferred unit.
const formatGranularTotal = (value: any) => {
  if (getUserUnit() === 'US') {
    return `${parseFloat(value).toLocaleString('en-US')} ${MEASURES.LBS}`;
  }
  return `${lbsToKg(value)} ${MEASURES.KG}`;
};

const groundSpeedFormatter = (value: number) => {
  const speedInMPH = (value * 0.0114);
  const speed      = convertSpeed(speedInMPH, Unit.IMPERIAL, getUserUnit());
  return `${parseFloat(speed.toFixed(2))} ${isImperial(getUserUnit()) ? 'mph' : 'kmph'}`;
};

const gateHeightFormatter = (value: string) => {
  const [height, unit] = value?.split(' ');
  if (!isNaN(Number(height))) {
    const uom       = unit?.toLowerCase() === 'cm' ? Unit.METRIC : Unit.IMPERIAL;
    const converted = convertHeight(Number(height), uom, getUserUnit());
    return `${parseFloat(converted.toFixed(3))} ${isImperial(getUserUnit()) ? 'in' : 'cm'}`;
  }
  return value;
};

const flowRateFormatter = (value: string, channel: 'CHANNEL' | 'CHANNEL_2') => {
  const isImperialUnit = isImperial(getUserUnit());
  const roundedValue = parseFloat(value) * 5280;
  if (channel === 'CHANNEL_2') {
    const rate = isImperialUnit ? roundedValue : (roundedValue * 3.78541) / 1.60934;
    return `${parseFloat(rate.toFixed(4))} ${isImperialUnit ? 'gal/mi' : 'L/km'}`;
  }
  const rate = isImperialUnit ? roundedValue : (roundedValue * 0.453592) / 1.60934;
  return `${parseFloat(rate.toFixed(4))} ${isImperialUnit ? 'lbs/mi' : 'kg/km'}`;
};

const defaultFormatter = (value: string) => (
  `${parseFloat(value).toLocaleString('en-US')} ${MEASURES.MILE}`
);

const temperatureFormatter = (value: string) => {
  const units         = getUserUnit() || Unit.US;
  const systemOfUnits = {value: units};
  const unit          = isEUProduction() ? 'F' : 'C';
  return `${genericUnitConversion(value, unit, systemOfUnits, 2)} ${getTemperatureUnit(units)}`;
};

const settingFormatter = measure => (value: string) => `${parseFloat(value)} ${measure}`;

// ForceAmericaTransformers is an object that will be used to transform
// the data for Force America Spreader based on the property type.
export const ForceAmericaTransformers = {
  granularSetRate       : (value: string) => `${parseFloat(value).toLocaleString('en-US')} ${MEASURES.LBS_MILE}`,
  granularMaterialTotal : (value: string) => `${formatGranularTotal(value)}`,
  prewetVolumeTotal     : (value: string) => `${parseFloat(value)} ${MEASURES.GAL}`,
  directLiquidTotal     : (value: string) => `${parseFloat(value)} ${MEASURES.GAL}`,
  dustTotal             : (value: string) => `${parseFloat(value)} ${MEASURES.LBS}`,
  airTemperature        : temperatureFormatter,
  roadTemperature       : temperatureFormatter
};

// CirusTransformers is an object that will be used to transform
// the data for Cirus Spreader based on the property type.
export const CirusFormatters = {
  granular        : (value: string) => `${formatGranularTotal(value)}`,
  prewetSetting   : settingFormatter(MEASURES.GAL),
  antiiceSetting  : settingFormatter(MEASURES.GAL),
  dryDistance     : defaultFormatter,
  vehicleDistance : defaultFormatter,
  airTemperature  : temperatureFormatter,
  roadTemperature : temperatureFormatter
};

export const DickeyJohnTransformers = {
  targetApplicationRate : flowRateFormatter,
  actualApplicationRate : flowRateFormatter,
  granularTotal         : (value: string) => `${formatGranularTotal(value)}`,
  groundSpeed           : groundSpeedFormatter,
  gateHeight            : gateHeightFormatter,
  airTemperature        : temperatureFormatter,
  roadTemperature       : temperatureFormatter
};

export const getMssStatus = (status) => {
  if (status === MSS_STATUS.OFF) {
    return translate('tracking:spreader.DICKEY_JOHN.statusOff');
  } else if (status === MSS_STATUS.AUTO) {
    return translate('tracking:spreader.DICKEY_JOHN.statusAuto');
  } else if (status === MSS_STATUS.UNLOAD) {
    return translate('tracking:spreader.DICKEY_JOHN.statusUnload');
  } else return translate('tracking:spreader.DICKEY_JOHN.statusUnknown');
};

export const formatData = (data: DickeyJohnFormatterProps | GenericObject) => {
  const {type, value, channel} = data || {};
  if (DickeyJohnTransformers[type]) {
    return DickeyJohnTransformers[type](value, channel).toString();
  }
  return '';
};

// formatSpreaderData is a function that is used to format the data
// for the Force America Spreader.
export const formatForceAmericaData = (type: string, value: GenericObject) => {
  if (ForceAmericaTransformers[type]) {
    return ForceAmericaTransformers[type](value).toString();
  }
  return value;
};

// formatSpreaderData is a function that is used to format the data
// for the Force America Spreader.
export const formatCirusData = (type: string, value: GenericObject) => {
  const implementation = CirusFormatters[type];
  return implementation ? implementation?.(value).toString() : value;
};

export const formatGroundValue = (data: GenericObject) => {
  const key   = Object.keys(data)[0];
  const value = Object.values(data)[0] as never;
  if (key === 'groundSpeed' || key === 'gateHeight') {
    return DickeyJohnTransformers[key](value);
  }
  return Object.values(data)[0];
};

export const getAccumulatorDataLable = (setting: string, type: string, units: string) => {
  if (setting.toLowerCase() === 'vehicle') {
    return translate('tracking:spreader.DICKEY_JOHN.distanceTravelled');
  }
  const unitsTranslated = translate(`tracking:spreader.DICKEY_JOHN.unitsFor.${units?.toLowerCase()}`);
  const typeTranslated  = translate(`tracking:spreader.DICKEY_JOHN.type.${type?.toLowerCase()}`);
  return `${typeTranslated} ${unitsTranslated}`;
};

export const getAccumulatorDataValue = (setting: string, value: string, units: string) => {
  let userPreferedValue: any = value;
  const unitInLowerCase = units?.toLowerCase();
  let unitsTranslated   = unitInLowerCase;
  switch (unitInLowerCase) {
    case 'km':
    case 'miles': {
      const uom = unitInLowerCase === 'km' ? Unit.METRIC : Unit.IMPERIAL;
      userPreferedValue = parseFloat(convertDistance(Number(value), uom, getUserUnit()).toFixed(2));
      unitsTranslated   = isImperial(getUserUnit()) ? Measurement.US : Measurement.METRIC;
      break;
    }
    case 'l':
    case 'gal' : {
      const uom = unitInLowerCase === 'l' ? Unit.METRIC : Unit.IMPERIAL;
      userPreferedValue = parseFloat(convertVolume(Number(value), uom, getUserUnit()).toFixed(2));
      unitsTranslated   = isImperial(getUserUnit()) ? MEASURES.GAL : MEASURES.LITER;
      break;
    }
    case 'tons': {
      userPreferedValue = parseFloat(convertWeight(Number(value) * 1000, Unit.METRIC, getUserUnit()).toFixed(2));
      unitsTranslated   = isImperial(getUserUnit()) ? MEASURES.LBS : MEASURES.KG;
      break;
    }
    case 'hours':
      unitsTranslated = MEASURES.HOURS;
      break;
    default:
      break;
  }
  if (setting.toLowerCase() === 'vehicle') {
    return `${userPreferedValue} ${unitsTranslated}`.trim();
  }
  const settingTranslated = translate(`tracking:spreader.DICKEY_JOHN.setting.${setting?.toLowerCase()}`);
  return `${userPreferedValue}${unitsTranslated ? ` ${unitsTranslated}` : ''} ${settingTranslated}`;
};
